import React, { useState, useEffect, useMemo } from "react";
import {
  X,
  Plus,
  MessageSquare,
  AlertCircle,
  Bug,
  Sparkles,
  Workflow,
  FileText,
  Palette,
  Send,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import storage, { KEYS } from "../lib/storage.js";

const CATEGORIES = [
  { id: "bug", label: "Bug", icon: Bug, color: "rose" },
  { id: "enhancement", label: "Enhancement", icon: Sparkles, color: "sky" },
  { id: "workflow", label: "Workflow", icon: Workflow, color: "amber" },
  { id: "content", label: "Content / Citation", icon: FileText, color: "stone" },
  { id: "design", label: "Design / UX", icon: Palette, color: "emerald" },
];

const PRIORITIES = [
  { id: "low", label: "Low" },
  { id: "normal", label: "Normal" },
  { id: "high", label: "High" },
  { id: "urgent", label: "Urgent" },
];

const STATUSES = [
  { id: "open", label: "Open", color: "bg-rose-100 text-rose-800" },
  { id: "in_progress", label: "In Progress", color: "bg-amber-100 text-amber-800" },
  { id: "closed", label: "Closed", color: "bg-emerald-100 text-emerald-800" },
  { id: "wontfix", label: "Won't Fix", color: "bg-stone-200 text-stone-700" },
];

function uid(prefix) {
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
}

export default function FeedbackPanel({ submitterName, onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterStatus, setFilterStatus] = useState("open");
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const raw = await storage.get(KEYS.FEEDBACK, true);
      if (!mounted) return;
      try {
        const parsed = raw?.value ? JSON.parse(raw.value) : [];
        setItems(Array.isArray(parsed) ? parsed : []);
      } catch (e) {
        setItems([]);
      }
      setLoading(false);
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const filtered = useMemo(() => {
    return items
      .filter((item) => {
        if (filterCategory !== "all" && item.category !== filterCategory) return false;
        if (filterStatus !== "all" && item.status !== filterStatus) return false;
        return true;
      })
      .sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || ""));
  }, [items, filterCategory, filterStatus]);

  async function persist(nextItems) {
    setItems(nextItems);
    await storage.set(KEYS.FEEDBACK, JSON.stringify(nextItems), true);
  }

  async function handleNewSubmit(form) {
    const newItem = {
      id: uid("fb"),
      title: form.title,
      description: form.description,
      category: form.category,
      priority: form.priority,
      status: "open",
      submitter: submitterName || "Anonymous",
      area: form.area,
      createdAt: new Date().toISOString(),
      responses: [],
    };
    await persist([newItem, ...items]);
    setShowNew(false);
  }

  async function handleStatusChange(id, status) {
    const next = items.map((it) => (it.id === id ? { ...it, status } : it));
    await persist(next);
  }

  async function handleAddResponse(id, text) {
    if (!text.trim()) return;
    const next = items.map((it) =>
      it.id === id
        ? {
            ...it,
            responses: [
              ...(it.responses || []),
              {
                author: submitterName || "Anonymous",
                text: text.trim(),
                date: new Date().toISOString(),
              },
            ],
          }
        : it,
    );
    await persist(next);
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/70 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-sm shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-stone-300 flex items-center justify-between bg-gradient-to-r from-stone-900 to-sky-900">
          <div>
            <div className="serif text-xl font-bold text-white flex items-center gap-2">
              <MessageSquare size={20} className="text-sky-300" />
              Firm Feedback
            </div>
            <div className="sans text-xs text-sky-200 mt-0.5">
              Bugs, enhancements, workflow notes, content corrections — visible to everyone
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowNew(true)}
              className="flex items-center gap-1.5 bg-sky-400 text-stone-900 px-3 py-1.5 rounded-sm sans text-sm font-bold hover:bg-sky-300"
            >
              <Plus size={14} />
              New Feedback
            </button>
            <button onClick={onClose} className="text-white p-1">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="px-6 py-3 border-b border-stone-200 flex items-center gap-3 bg-stone-50">
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-3 py-1.5 border border-stone-300 bg-white rounded-sm sans text-sm focus:outline-none focus:border-sky-500"
          >
            <option value="all">All categories</option>
            {CATEGORIES.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-1.5 border border-stone-300 bg-white rounded-sm sans text-sm focus:outline-none focus:border-sky-500"
          >
            <option value="all">All statuses</option>
            {STATUSES.map((s) => (
              <option key={s.id} value={s.id}>
                {s.label}
              </option>
            ))}
          </select>
          <div className="flex-1 sans text-xs text-stone-500 text-right">
            {loading ? "Loading…" : `${filtered.length} item${filtered.length === 1 ? "" : "s"}`}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto px-6 py-4">
          {showNew && (
            <NewFeedbackForm
              onSubmit={handleNewSubmit}
              onCancel={() => setShowNew(false)}
            />
          )}

          {!loading && filtered.length === 0 && !showNew && (
            <div className="text-center py-12">
              <MessageSquare size={36} className="text-stone-300 mx-auto mb-3" />
              <div className="serif text-lg font-bold text-stone-700 mb-1">
                No feedback yet
              </div>
              <div className="sans text-sm text-stone-500">
                Click "New Feedback" to be the first to submit a note.
              </div>
            </div>
          )}

          <div className="space-y-3">
            {filtered.map((item) => (
              <FeedbackItem
                key={item.id}
                item={item}
                expanded={expandedId === item.id}
                onToggle={() =>
                  setExpandedId(expandedId === item.id ? null : item.id)
                }
                onStatusChange={(status) => handleStatusChange(item.id, status)}
                onAddResponse={(text) => handleAddResponse(item.id, text)}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function NewFeedbackForm({ onSubmit, onCancel }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("bug");
  const [priority, setPriority] = useState("normal");
  const [area, setArea] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    onSubmit({ title: title.trim(), description: description.trim(), category, priority, area: area.trim() });
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-sky-50/40 border border-sky-200 rounded-sm p-4 mb-4"
    >
      <div className="serif text-lg font-bold text-stone-900 mb-3">New feedback item</div>
      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-12">
          <label className="sans text-[10px] uppercase tracking-widest font-semibold text-stone-500 mb-1 block">
            Title
          </label>
          <input
            autoFocus
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            placeholder="Short summary — e.g. Augusta rule cap not applied at day 14"
            className="w-full px-3 py-2 border border-stone-300 bg-white rounded-sm sans text-sm focus:outline-none focus:border-sky-500"
          />
        </div>
        <div className="col-span-4">
          <label className="sans text-[10px] uppercase tracking-widest font-semibold text-stone-500 mb-1 block">
            Category
          </label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="w-full px-3 py-2 border border-stone-300 bg-white rounded-sm sans text-sm focus:outline-none focus:border-sky-500"
          >
            {CATEGORIES.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>
        </div>
        <div className="col-span-4">
          <label className="sans text-[10px] uppercase tracking-widest font-semibold text-stone-500 mb-1 block">
            Priority
          </label>
          <select
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
            className="w-full px-3 py-2 border border-stone-300 bg-white rounded-sm sans text-sm focus:outline-none focus:border-sky-500"
          >
            {PRIORITIES.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </select>
        </div>
        <div className="col-span-4">
          <label className="sans text-[10px] uppercase tracking-widest font-semibold text-stone-500 mb-1 block">
            Area (optional)
          </label>
          <input
            value={area}
            onChange={(e) => setArea(e.target.value)}
            placeholder="e.g. Strategy Picker"
            className="w-full px-3 py-2 border border-stone-300 bg-white rounded-sm sans text-sm focus:outline-none focus:border-sky-500"
          />
        </div>
        <div className="col-span-12">
          <label className="sans text-[10px] uppercase tracking-widest font-semibold text-stone-500 mb-1 block">
            Description
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What you saw, expected, steps to reproduce, screenshots in Slack, etc."
            rows={4}
            className="w-full px-3 py-2 border border-stone-300 bg-white rounded-sm sans text-sm focus:outline-none focus:border-sky-500 resize-y"
          />
        </div>
        <div className="col-span-12 flex justify-end gap-2 mt-1">
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-2 sans text-sm text-stone-600 hover:text-stone-900"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!title.trim()}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-sm sans text-sm font-semibold ${
              !title.trim()
                ? "bg-stone-200 text-stone-400 cursor-not-allowed"
                : "bg-stone-900 text-sky-400 hover:bg-stone-800"
            }`}
          >
            <Send size={14} />
            Submit
          </button>
        </div>
      </div>
    </form>
  );
}

function FeedbackItem({ item, expanded, onToggle, onStatusChange, onAddResponse }) {
  const [response, setResponse] = useState("");
  const cat = CATEGORIES.find((c) => c.id === item.category) || CATEGORIES[0];
  const Icon = cat.icon;
  const status = STATUSES.find((s) => s.id === item.status) || STATUSES[0];
  const priority = PRIORITIES.find((p) => p.id === item.priority);
  const responseCount = (item.responses || []).length;
  const dateStr = new Date(item.createdAt).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  const iconColors = {
    rose: "text-rose-600",
    sky: "text-sky-600",
    amber: "text-amber-700",
    stone: "text-stone-600",
    emerald: "text-emerald-600",
  };

  const submit = (e) => {
    e.preventDefault();
    if (!response.trim()) return;
    onAddResponse(response);
    setResponse("");
  };

  return (
    <div className="border border-stone-200 rounded-sm bg-white">
      <button
        onClick={onToggle}
        className="w-full px-4 py-3 flex items-start gap-3 text-left hover:bg-stone-50"
      >
        <div className="mt-0.5">
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </div>
        <Icon size={18} className={`${iconColors[cat.color] || "text-stone-600"} mt-0.5 shrink-0`} />
        <div className="flex-1 min-w-0">
          <div className="sans text-sm font-semibold text-stone-900">{item.title}</div>
          <div className="sans text-[11px] text-stone-500 mt-0.5 flex items-center gap-2 flex-wrap">
            <span className="font-semibold">{cat.label}</span>
            {priority && <span>· {priority.label} priority</span>}
            <span>· by {item.submitter}</span>
            <span>· {dateStr}</span>
            {item.area && <span>· {item.area}</span>}
            {responseCount > 0 && (
              <span className="text-sky-700 font-semibold">· {responseCount} response{responseCount === 1 ? "" : "s"}</span>
            )}
          </div>
        </div>
        <span
          className={`shrink-0 sans text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-sm ${status.color}`}
        >
          {status.label}
        </span>
      </button>

      {expanded && (
        <div className="px-4 pb-4 border-t border-stone-200">
          {item.description && (
            <div className="sans text-sm text-stone-700 leading-relaxed py-3 whitespace-pre-wrap">
              {item.description}
            </div>
          )}

          {/* Status changer */}
          <div className="flex items-center gap-2 mb-3">
            <span className="sans text-[10px] uppercase tracking-widest font-semibold text-stone-500">
              Set status:
            </span>
            {STATUSES.map((s) => (
              <button
                key={s.id}
                onClick={() => onStatusChange(s.id)}
                className={`sans text-[11px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-sm ${
                  item.status === s.id ? s.color : "bg-stone-100 text-stone-500 hover:bg-stone-200"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>

          {/* Responses */}
          {responseCount > 0 && (
            <div className="space-y-2 mb-3">
              {item.responses.map((r, i) => (
                <div key={i} className="border-l-2 border-sky-300 pl-3 py-1">
                  <div className="sans text-[11px] text-stone-500 mb-0.5">
                    <strong className="text-stone-700">{r.author}</strong> ·{" "}
                    {new Date(r.date).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </div>
                  <div className="sans text-sm text-stone-800 whitespace-pre-wrap">{r.text}</div>
                </div>
              ))}
            </div>
          )}

          {/* Add response */}
          <form onSubmit={submit} className="flex gap-2">
            <input
              value={response}
              onChange={(e) => setResponse(e.target.value)}
              placeholder="Reply…"
              className="flex-1 px-3 py-2 border border-stone-300 bg-white rounded-sm sans text-sm focus:outline-none focus:border-sky-500"
            />
            <button
              type="submit"
              disabled={!response.trim()}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-sm sans text-sm font-semibold ${
                !response.trim()
                  ? "bg-stone-200 text-stone-400 cursor-not-allowed"
                  : "bg-stone-900 text-sky-400 hover:bg-stone-800"
              }`}
            >
              <Send size={14} />
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
